import { skipToken } from '@reduxjs/toolkit/query/react';
import { useEffect, useState } from 'react';
import { useUserAccount } from '../configuration/login/loginSlice';
import { useFetchEulaStatusQuery } from '../services/iamApi';
import { useFetchAllRelevantAssetsQuery } from '../services/assetsApi';
import { iframeMessageType, isRemindMeLaterActive, isRunningInIframe, sendMessageToParent } from '../utils';

const FT_MOCK_VALUE_KEY = 'mockNumberOfAssets';
const getMockNumberOfAssets = () => {
    let mockValue = null;
    try {
        mockValue = localStorage.getItem(FT_MOCK_VALUE_KEY);
    } catch (e) {
        console.warn(e);
    }
    if (mockValue && !Number.isNaN(Number.parseInt(mockValue, 10))) {
        return Number.parseInt(mockValue, 10);
    } else {
        return null;
    }
};

export const MOCK_NUMBER_OF_ASSETS = getMockNumberOfAssets();

const isInAssetAdministration = () => {
    if (!isRunningInIframe()) {
        return false;
    }

    try {
        return 'administration.assets.rio.cloud' === window.top?.location.hostname;
    } catch (e) {
        return false;
    }
};

export const useLoadEulaStatusAndCommunicateWithParentIframe = () => {
    const [displayNull, setDisplayNull] = useState(isRunningInIframe());

    const accountId = useUserAccount();
    const isAccountIdMissing = !accountId;

    const {
        data: eulaStatus,
        isLoading: isEulaStatusLoading,
        isError: isEulaStatusError,
    } = useFetchEulaStatusQuery(accountId ?? skipToken);

    const {
        data: assets,
        isLoading: isAssetsLoading,
        isError: isAssetsError,
    } = useFetchAllRelevantAssetsQuery(eulaStatus?.status === 'UNKNOWN' ? undefined : skipToken);

    useEffect(() => {
        if (isRemindMeLaterActive()) {
            sendMessageToParent({
                type: iframeMessageType.REMIND_ME_LATER_COOKIE_FOUND,
            });
        }
    }, []);

    useEffect(() => {
        const nextDisplayNull =
            isRunningInIframe() &&
            (isRemindMeLaterActive() ||
                // EULA should not be displayed if the EULA status is ACCEPTED, DECLINED, or NOT_APPLICABLE,
                (eulaStatus && ['ACCEPTED', 'DECLINED', 'NOT_APPLICABLE'].includes(eulaStatus.status)) ||
                // EULA should not be displayed even if status is UNKNOWN if there are no relevant assets
                // (eulaStatus?.status === 'UNKNOWN' && !assets?.length) ||
                (eulaStatus?.status === 'UNKNOWN' && typeof MOCK_NUMBER_OF_ASSETS === 'number'
                    ? !MOCK_NUMBER_OF_ASSETS
                    : !assets?.length) ||
                // EULA should not be displayed if the status is still loading or if there was an error fetching the status
                isEulaStatusLoading ||
                isEulaStatusError ||
                isAssetsLoading ||
                isAssetsError);
        setDisplayNull(nextDisplayNull);
    }, [eulaStatus, isEulaStatusLoading, isEulaStatusError, assets]);

    useEffect(() => {
        sendMessageToParent({
            type: displayNull
                ? iframeMessageType.PAGE_WITHOUT_CONTENT_LOADED
                : iframeMessageType.PAGE_WITH_CONTENT_LOADED,
        });
    }, [displayNull]);

    useEffect(() => {
        let numberOfRelevantAssets = assets?.length;
        if (numberOfRelevantAssets !== undefined) {
            if (MOCK_NUMBER_OF_ASSETS) {
                console.log('[RLN] Using mock number of assets:', MOCK_NUMBER_OF_ASSETS);
                numberOfRelevantAssets = MOCK_NUMBER_OF_ASSETS;
            }
        }

        const isAssetAdministration = isInAssetAdministration();
        if (isAssetAdministration) {
            console.log('[RLN] Prevent the EULA from being displayed in the fleet administration.:');
        }

        sendMessageToParent({
            type: iframeMessageType.EULA_STATUS_RECEIVED,
            payload: {
                eulaStatus: eulaStatus?.status,
                isError: isEulaStatusError || isAssetsError || isAccountIdMissing || isAssetAdministration,
                numberOfRelevantAssets: numberOfRelevantAssets,
            },
        });
    }, [eulaStatus, isEulaStatusError, assets, isAssetsError, isAccountIdMissing]);

    return { displayNull, isLoading: isEulaStatusLoading || isAssetsLoading };
};
